import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWS } from '../contexts/WebSocketContext';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import MessageActions from '../components/chat/MessageActions';
import EmojiPicker from '../components/chat/EmojiPicker';
import ThreadPanel from '../components/chat/ThreadPanel';
import DateDivider from '../components/chat/DateDivider';
import ChannelSettings from '../components/chat/ChannelSettings';
import UserStatusSelector from '../components/chat/UserStatusSelector';
import NotificationSettings from '../components/chat/NotificationSettings';
import PresenceIndicator from '../components/chat/PresenceIndicator';
import AdminPanel from '../components/chat/AdminPanel';
import AdvancedSearch from '../components/chat/AdvancedSearch';
import SavedMessages from '../components/chat/SavedMessages';
import { AnnouncementBanner, AnnouncementMessage, CreateAnnouncementDialog } from '../components/chat/Announcements';
import { VoiceCallDialog, VideoCallDialog, HuddleDialog, QuickCallButtons } from '../components/chat/VoiceVideoHuddle';
import { QuickCallButtons as VideoQuickCallButtons, PreCallSettings } from '../components/chat/VideoCallWindow';
import { useVideoCallContext } from '../contexts/VideoCallContext';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Hash, 
  Plus, 
  Search, 
  Send, 
  Paperclip, 
  Smile, 
  MoreVertical,
  Users,
  Pin,
  FileText,
  Sparkles,
  Lock,
  ChevronDown,
  MessageSquare,
  X,
  Image as ImageIcon,
  File as FileIcon,
  Download,
  Eye,
  Settings,
  Bell,
  Star,
  Archive,
  Shield,
  Bookmark,
  Megaphone,
  Filter,
  Phone,
  Video,
  Briefcase,
  User,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Chat = () => {
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Check if user is viewing as another user (impersonation mode)
  const isImpersonating = localStorage.getItem('is_impersonating') === 'true';
  const ws = useWS();
  const [loading, setLoading] = useState(true);
  const [channels, setChannels] = useState([]);
  const [directMessages, setDirectMessages] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const messagesEndRef = useRef(null);
  const previousChannelRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Track if we've handled the channel param from URL
  const channelParamHandledRef = useRef(false);

  // Thread state
  const [threadOpen, setThreadOpen] = useState(false);
  const [threadParentMessage, setThreadParentMessage] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadReplyText, setThreadReplyText] = useState('');
  const [threadCounts, setThreadCounts] = useState({});
  
  // Phase 1: Edit message state
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState('');

  // Reactions
  const [messageReactions, setMessageReactions] = useState({});

  // File upload
  const [uploadingFile, setUploadingFile] = useState(false);

  // Users and DM Dialog
  const [users, setUsers] = useState([]);
  const [newDMDialogOpen, setNewDMDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');

  // Search
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Mentions
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [mentionSearchText, setMentionSearchText] = useState('');

  // AI Summary
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [summary, setSummary] = useState('');
  const [generatingSummary, setGeneratingSummary] = useState(false);

  // Read Receipts
  const [readReceiptsDialogOpen, setReadReceiptsDialogOpen] = useState(false);
  const [selectedMessageForReceipts, setSelectedMessageForReceipts] = useState(null);
  const [readReceipts, setReadReceipts] = useState([]);

  // Phase 2: Channel Settings & Preferences
  const [channelSettingsOpen, setChannelSettingsOpen] = useState(false);
  const [notificationSettingsOpen, setNotificationSettingsOpen] = useState(false);
  const [userPreferences, setUserPreferences] = useState({
    starred_channels: [],
    muted_channels: []
  });
  const [userStatus, setUserStatus] = useState(null);
  
  // Phase 3: Advanced Search, Saved Messages, Announcements
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  const [savedMessagesOpen, setSavedMessagesOpen] = useState(false);
  const [createAnnouncementOpen, setCreateAnnouncementOpen] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  
  // Phase 4: Admin Panel
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [chatPermissions, setChatPermissions] = useState(null);

  // Phase 5: Voice, Video, Huddles (Future-ready)
  const [voiceCallOpen, setVoiceCallOpen] = useState(false);
  const [videoCallOpen, setVideoCallOpen] = useState(false);
  const [huddleOpen, setHuddleOpen] = useState(false);
  const [showPreCallSettings, setShowPreCallSettings] = useState(false);
  const [pendingCallTarget, setPendingCallTarget] = useState(null);
  
  // Phase 5+: WebRTC Video Calls - use global context
  const videoCall = useVideoCallContext();

  const [channelForm, setChannelForm] = useState({
    name: '',
    description: '',
    type: 'public',
    members: []
  });

  useEffect(() => {
    fetchChannels();
    fetchDirectMessages();
    fetchUsers();
    fetchUserPreferences();
    fetchUserStatus();
    fetchChatPermissions();
    fetchAnnouncements();
  }, []);

  useEffect(() => {
    if (selectedChannel) {
      fetchMessages(selectedChannel.id);
      fetchThreadCounts();
    }
  }, [selectedChannel]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // WebSocket: Join/leave channels when selection changes
  useEffect(() => {
    if (!ws.isConnected || !selectedChannel) return;

    // Leave previous channel
    if (previousChannelRef.current) {
      ws.leaveChannel(previousChannelRef.current.id);
    }

    // Join new channel
    ws.joinChannel(selectedChannel.id);
    
    // Mark channel as read
    ws.sendReadStatus(selectedChannel.id);
    ws.clearUnreadCount(selectedChannel.id);
    
    // Mark notifications as read for this channel
    markChannelNotificationsRead(selectedChannel.id);

    previousChannelRef.current = selectedChannel;

    return () => {
      if (selectedChannel) {
        ws.leaveChannel(selectedChannel.id);
      }
    };
  }, [selectedChannel, ws.isConnected]);

  // WebSocket: Handle incoming messages
  useEffect(() => {
    if (!ws.messages || ws.messages.length === 0) return;

    const latestMessage = ws.messages[ws.messages.length - 1];
    
    // Add message to current channel if it matches
    if (selectedChannel && latestMessage.channel_id === selectedChannel.id) {
      setMessages(prev => {
        // Avoid duplicates
        if (prev.some(m => m.id === latestMessage.id)) {
          return prev;
        }
        return [...prev, latestMessage];
      });
    }

    // Note: We don't need to refetch all channels on every message
    // Unread counts are managed by WebSocket context
  }, [ws.messages, selectedChannel]);

  // WebSocket: Handle reactions
  useEffect(() => {
    if (!ws.lastMessage) return;
    
    const { type, message_id, reactions } = ws.lastMessage;
    
    if (type === 'reaction' && message_id && reactions) {
      setMessageReactions(prev => ({
        ...prev,
        [message_id]: reactions
      }));
    }
  }, [ws.lastMessage]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChannels = async () => {
    try {
      const response = await axios.get(`${API}/chat/channels`);
      const allChannels = response.data.channels || [];
      
      // Only set non-DM channels - DMs are fetched separately with more details
      setChannels(allChannels.filter(c => c.type !== 'direct'));
      
      // Check for channel param in URL (deep-link from admin widget)
      const channelIdFromUrl = searchParams.get('channel');
      if (channelIdFromUrl && !channelParamHandledRef.current) {
        channelParamHandledRef.current = true;
        const targetChannel = allChannels.find(c => c.id === channelIdFromUrl);
        if (targetChannel) {
          setSelectedChannel(targetChannel);
          // Clear the URL param after handling
          setSearchParams({}, { replace: true });
          return; // Exit early, don't auto-select first channel
        }
      }
      
      // Auto-select first channel if none selected
      if (!selectedChannel && allChannels.length > 0) {
        const nonDmChannel = allChannels.find(c => c.type !== 'direct');
        if (nonDmChannel) {
          setSelectedChannel(nonDmChannel);
        }
      }
    } catch (error) {
      console.error('Error fetching channels:', error);
      toast.error('Failed to load channels');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (channelId) => {
    try {
      const response = await axios.get(`${API}/chat/channels/${channelId}/messages`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !selectedChannel) return;

    // Stop typing indicator
    if (isTyping) {
      ws.sendTyping(selectedChannel.id, `${user.first_name} ${user.last_name}`.trim() || user.email, false);
      setIsTyping(false);
    }

    // Optimistic UI update
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      channel_id: selectedChannel.id,
      sender_id: user.id,
      sender_name: `${user.first_name} ${user.last_name}`.trim() || user.email,
      sender_role: user.role,
      content: newMessage,
      attachments: [],
      created_at: new Date().toISOString(),
      pending: true
    };

    const messageContent = newMessage;
    setMessages(prev => [...prev, optimisticMessage]);
    setNewMessage('');

    try {
      const response = await axios.post(`${API}/chat/channels/${selectedChannel.id}/messages`, {
        content: optimisticMessage.content,
        attachments: []
      });

      // Replace optimistic message with real one
      setMessages(prev => prev.map(m => 
        m.id === optimisticMessage.id ? response.data : m
      ));
      
      // Check if this is a DM and there might be an active Prime auto-reply session
      if (selectedChannel.type === 'direct') {
        try {
          // Check for active Prime auto-reply session
          const sessionsRes = await axios.get(`${API}/prime/auto-reply/active-sessions`);
          const activeSession = sessionsRes.data.sessions?.find(s => s.channel_id === selectedChannel.id);
          
          if (activeSession) {
            // There's an active Prime session - send the message to Prime for handling
            const primeRes = await axios.post(`${API}/prime/auto-reply/handle-response`, {
              channel_id: selectedChannel.id,
              message: messageContent,
              user_id: user.id
            });
            
            // Add Prime's response to the chat immediately
            if (primeRes.data.success && primeRes.data.response) {
              // Fetch the latest messages to get Prime's response
              setTimeout(async () => {
                try {
                  const msgsRes = await axios.get(`${API}/chat/channels/${selectedChannel.id}/messages`);
                  setMessages(msgsRes.data.messages || msgsRes.data);
                } catch (err) {
                  console.error('Error refreshing messages:', err);
                }
              }, 500);
            }
          }
        } catch (primeErr) {
          // Prime auto-reply check failed, that's okay - normal message flow continues
          console.log('No active Prime auto-reply session:', primeErr);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // WebSocket will broadcast the message if it was saved
      // Remove the optimistic message only if it doesn't get confirmed via WebSocket within 2 seconds
      setTimeout(() => {
        setMessages(prev => {
          const stillPending = prev.find(m => m.id === optimisticMessage.id && m.pending);
          if (stillPending) {
            // Message still pending after 2 seconds, likely failed
            return prev.filter(m => m.id !== optimisticMessage.id);
          }
          return prev;
        });
      }, 2000);
    }
  };

  const handleTyping = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    // Check for mentions
    handleMentionInput(value);

    if (!selectedChannel || !ws.isConnected) return;

    const userName = `${user.first_name} ${user.last_name}`.trim() || user.email;

    // Clear existing timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    // Send typing start
    if (!isTyping && value.length > 0) {
      setIsTyping(true);
      ws.sendTyping(selectedChannel.id, userName, true);
    }

    // Set timeout to send typing stop
    const timeout = setTimeout(() => {
      setIsTyping(false);
      ws.sendTyping(selectedChannel.id, userName, false);
    }, 3000);

    setTypingTimeout(timeout);
  };

  // Thread functions
  const openThread = async (message) => {
    setThreadParentMessage(message);
    setThreadOpen(true);
    
    try {
      const response = await axios.get(`${API}/chat/messages/${message.id}/thread`);
      setThreadMessages(response.data.replies || []);
    } catch (error) {
      console.error('Error fetching thread:', error);
      toast.error('Failed to load thread');
    }
  };

  const closeThread = () => {
    setThreadOpen(false);
    setThreadParentMessage(null);
    setThreadMessages([]);
    setThreadReplyText('');
  };

  const sendThreadReply = async (e) => {
    e.preventDefault();
    
    if (!threadReplyText.trim() || !threadParentMessage) return;

    try {
      const response = await axios.post(`${API}/chat/channels/${selectedChannel.id}/messages`, {
        content: threadReplyText,
        attachments: [],
        thread_id: threadParentMessage.id
      });

      setThreadMessages(prev => [...prev, response.data]);
      setThreadReplyText('');
      
      // Update thread counts
      setThreadCounts(prev => ({
        ...prev,
        [threadParentMessage.id]: (prev[threadParentMessage.id] || 0) + 1
      }));
    } catch (error) {
      console.error('Error sending thread reply:', error);
      toast.error('Failed to send reply');
    }
  };

  const fetchThreadCounts = async () => {
    if (!selectedChannel) return;
    
    try {
      const response = await axios.get(`${API}/chat/channels/${selectedChannel.id}/thread-counts`);
      setThreadCounts(response.data.thread_counts || {});
    } catch (error) {
      console.error('Error fetching thread counts:', error);
    }
  };

  // Reaction functions
  const toggleReaction = async (messageId, emoji) => {
    try {
      const response = await axios.post(`${API}/chat/messages/${messageId}/reactions`, {
        emoji
      });

      // Update local reactions
      setMessageReactions(prev => ({
        ...prev,
        [messageId]: response.data.reactions
      }));
    } catch (error) {
      console.error('Error toggling reaction:', error);
      toast.error('Failed to add reaction');
    }
  };

  // File upload functions
  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploadingFile(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/chat/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const attachment = response.data;

      // Send message with attachment
      await axios.post(`${API}/chat/channels/${selectedChannel.id}/messages`, {
        content: `Shared ${attachment.file_type}: ${attachment.filename}`,
        attachments: [attachment]
      });

      toast.success('File uploaded successfully');
      fetchMessages(selectedChannel.id);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
      e.target.value = ''; // Reset file input
    }
  };

  // Direct Messages functions
  const fetchDirectMessages = async () => {
    try {
      const response = await axios.get(`${API}/chat/direct-messages`);
      setDirectMessages(response.data.direct_messages || []);
    } catch (error) {
      console.error('Error fetching DMs:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/chat/users`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Phase 2: Fetch user preferences (starred, muted channels)
  const fetchUserPreferences = async () => {
    try {
      const response = await axios.get(`${API}/chat/preferences`);
      setUserPreferences(response.data || { starred_channels: [], muted_channels: [] });
    } catch (error) {
      console.error('Error fetching user preferences:', error);
    }
  };

  // Phase 2: Fetch user status/presence
  const fetchUserStatus = async () => {
    try {
      const response = await axios.get(`${API}/chat/presence/${user?.id}`);
      setUserStatus(response.data);
    } catch (error) {
      console.error('Error fetching user status:', error);
    }
  };

  // Phase 4: Fetch chat permissions
  const fetchChatPermissions = async () => {
    try {
      const response = await axios.get(`${API}/chat/permissions`);
      console.log('Chat permissions fetched:', response.data);
      setChatPermissions(response.data);
    } catch (error) {
      console.error('Error fetching chat permissions:', error);
    }
  };

  // Phase 3: Fetch announcements
  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/chat/announcements`);
      setAnnouncements(response.data.announcements || []);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };

  // Phase 3: Acknowledge announcement
  const handleAcknowledgeAnnouncement = async (announcementId) => {
    try {
      await axios.post(`${API}/chat/announcements/${announcementId}/acknowledge`);
      setAnnouncements(announcements.map(a => 
        a.id === announcementId ? { ...a, user_acknowledged: true } : a
      ));
      toast.success('Announcement acknowledged');
    } catch (error) {
      toast.error('Failed to acknowledge announcement');
    }
  };

  // Phase 3: Save/unsave message
  const handleSaveMessage = async (message) => {
    try {
      await axios.post(`${API}/chat/messages/${message.id}/save`);
      toast.success('Message saved');
    } catch (error) {
      toast.error('Failed to save message');
    }
  };

  const createOrGetDM = async (userId) => {
    try {
      const response = await axios.post(`${API}/chat/direct-messages?other_user_id=${userId}`);
      setSelectedChannel(response.data);
      setNewDMDialogOpen(false);
      fetchDirectMessages();
    } catch (error) {
      console.error('Error creating DM:', error);
      toast.error('Failed to create direct message');
    }
  };

  // Search functions
  const handleSearch = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(`${API}/chat/search?query=${encodeURIComponent(query)}`);
      setSearchResults(response.data.results || []);
    } catch (error) {
      console.error('Error searching:', error);
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const jumpToMessage = (result) => {
    // Find and select the channel
    const channel = channels.find(c => c.id === result.channel_id) || 
                    directMessages.find(d => d.id === result.channel_id);
    
    if (channel) {
      setSelectedChannel(channel);
      setSearchOpen(false);
      setSearchQuery('');
      setSearchResults([]);
      
      // Scroll to message (future enhancement)
      toast.success('Jumped to message');
    }
  };

  // Mention functions
  const handleMentionInput = (text) => {
    // Check if @ was just typed
    const lastAtIndex = text.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const textAfterAt = text.substring(lastAtIndex + 1);
      const spaceIndex = textAfterAt.indexOf(' ');
      
      if (spaceIndex === -1 || spaceIndex > textAfterAt.length) {
        // Still typing mention
        setMentionSearchText(textAfterAt);
        const filtered = users.filter(u => 
          u.name.toLowerCase().includes(textAfterAt.toLowerCase()) ||
          u.username.toLowerCase().includes(textAfterAt.toLowerCase())
        ).slice(0, 5);
        setMentionSuggestions(filtered);
        setShowMentionSuggestions(true);
      } else {
        setShowMentionSuggestions(false);
      }
    } else {
      setShowMentionSuggestions(false);
    }
  };

  const insertMention = (user) => {
    const lastAtIndex = newMessage.lastIndexOf('@');
    const beforeMention = newMessage.substring(0, lastAtIndex);
    const afterMention = newMessage.substring(lastAtIndex);
    const textAfterAt = afterMention.substring(1);
    
    const newText = beforeMention + `@${user.username} `;
    setNewMessage(newText);
    setShowMentionSuggestions(false);
  };

  // Mark chat notifications as read when viewing channel
  const markChannelNotificationsRead = async (channelId) => {
    try {
      // Mark all unread chat notifications for this channel
      await axios.post(`${API}/chat/channels/${channelId}/mark-notifications-read`);
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  // AI Summary functions
  const generateSummary = async () => {
    if (!selectedChannel) return;

    setGeneratingSummary(true);
    setSummaryDialogOpen(true);

    try {
      const response = await axios.post(`${API}/chat/channels/${selectedChannel.id}/summarize`);
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Error generating summary:', error);
      toast.error('Failed to generate summary');
      setSummary('Failed to generate summary. Please try again.');
    } finally {
      setGeneratingSummary(false);
    }
  };

  // Read Receipts functions
  const showReadReceipts = async (message) => {
    setSelectedMessageForReceipts(message);
    setReadReceiptsDialogOpen(true);

    try {
      const response = await axios.get(`${API}/chat/messages/${message.id}/read-receipts`);
      setReadReceipts(response.data.readers || []);
    } catch (error) {
      console.error('Error fetching read receipts:', error);
      toast.error('Failed to load read receipts');
    }
  };


  // Phase 1 Feature Handlers
  
  // Edit message
  const handleEditMessage = (message) => {
    setEditingMessage(message);
    setEditText(message.content);
  };

  const saveEditMessage = async () => {
    if (!editText.trim() || !editingMessage) return;

    try {
      await axios.put(`${API}/chat/messages/${editingMessage.id}`, {
        content: editText
      });

      // Update local state
      setMessages(messages.map(m => 
        m.id === editingMessage.id 
          ? { ...m, content: editText, edited_at: new Date().toISOString() }
          : m
      ));

      setEditingMessage(null);
      setEditText('');
      toast.success('Message updated');
    } catch (error) {
      console.error('Error editing message:', error);
      toast.error('Failed to edit message');
    }
  };

  // Delete message
  const handleDeleteMessage = async (message) => {
    if (!window.confirm('Delete this message? This cannot be undone.')) return;

    try {
      await axios.delete(`${API}/chat/messages/${message.id}`);
      
      // Remove from local state
      setMessages(messages.filter(m => m.id !== message.id));
      toast.success('Message deleted');
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message');
    }
  };

  // React to message
  const handleReactToMessage = async (message, emoji) => {
    try {
      await axios.post(`${API}/chat/messages/${message.id}/reactions`, {
        emoji: emoji
      });

      // Update local state
      setMessages(messages.map(m => {
        if (m.id === message.id) {
          const reactions = m.reactions || [];
          const existingReaction = reactions.find(r => r.emoji === emoji);
          
          if (existingReaction) {
            // Add user to existing reaction
            if (!existingReaction.users.includes(user.id)) {
              existingReaction.users.push(user.id);
              existingReaction.count++;
            }
          } else {
            // Create new reaction
            reactions.push({
              emoji: emoji,
              users: [user.id],
              count: 1
            });
          }
          
          return { ...m, reactions };
        }
        return m;
      }));
    } catch (error) {
      console.error('Error reacting to message:', error);
      toast.error('Failed to add reaction');
    }
  };

  // Reply in thread
  const handleReplyInThread = (message) => {
    setThreadParentMessage(message);
    setThreadOpen(true);
  };

  // Pin message
  const handlePinMessage = async (message) => {
    try {
      if (message.pinned) {
        await axios.delete(`${API}/chat/channels/${selectedChannel.id}/pin/${message.id}`);
        toast.success('Message unpinned');
      } else {
        await axios.post(`${API}/chat/channels/${selectedChannel.id}/pin/${message.id}`);
        toast.success('Message pinned');
      }

      // Update local state
      setMessages(messages.map(m => 
        m.id === message.id 
          ? { ...m, pinned: !m.pinned }
          : m
      ));
    } catch (error) {
      console.error('Error pinning message:', error);
      toast.error('Failed to pin message');
    }
  };

  // Copy message link
  const handleCopyLink = (message) => {
    const link = `${window.location.origin}/chat?channel=${selectedChannel.id}&message=${message.id}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard');
  };

  // Group messages by date
  const groupMessagesByDate = (messages) => {
    const groups = [];
    let currentDate = null;

    messages.forEach((message, index) => {
      const messageDate = new Date(message.created_at).toDateString();
      
      if (messageDate !== currentDate) {
        groups.push({ type: 'date', date: message.created_at });
        currentDate = messageDate;
      }
      
      // Check if should group with previous message (same sender, within 5 minutes)
      const prevMessage = messages[index - 1];
      const shouldGroup = prevMessage && 
        prevMessage.sender_id === message.sender_id &&
        new Date(message.created_at) - new Date(prevMessage.created_at) < 5 * 60 * 1000;
      
      groups.push({ type: 'message', message, grouped: shouldGroup });
    });

    return groups;
  };

  const handleCreateChannel = async () => {
    if (!channelForm.name.trim()) {
      toast.error('Channel name is required');
      return;
    }

    try {
      await axios.post(`${API}/chat/channels`, channelForm);
      toast.success('Channel created successfully');
      setChannelDialogOpen(false);
      setChannelForm({ name: '', description: '', type: 'public', members: [] });
      fetchChannels();
    } catch (error) {
      console.error('Error creating channel:', error);
      toast.error('Failed to create channel');
    }
  };

  const getChannelIcon = (type) => {
    if (type === 'private') return <Lock className="h-4 w-4" />;
    if (type === 'direct') return <MessageSquare className="h-4 w-4" />;
    return <Hash className="h-4 w-4" />;
  };

  const filteredChannels = channels.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-muted-foreground">Loading chat...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>


      <div className={`fixed left-[280px] right-0 bottom-0 flex bg-background ${isImpersonating ? 'top-[113px]' : 'top-[73px]'}`}>
        {/* Channel Sidebar */}
        <div className="w-64 border-r bg-card flex flex-col h-full overflow-hidden">
          {/* Search */}
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search channels..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
          </div>

          {/* Quick Access */}
          <div className="p-3 border-b">
            <div className="flex items-center gap-1 flex-wrap">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 text-xs"
                onClick={() => setAdvancedSearchOpen(true)}
              >
                <Filter className="h-3 w-3 mr-1" />
                Search
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 text-xs"
                onClick={() => setSavedMessagesOpen(true)}
              >
                <Bookmark className="h-3 w-3 mr-1" />
                Saved
              </Button>
              {chatPermissions?.permissions?.is_admin && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 text-xs"
                  onClick={() => setCreateAnnouncementOpen(true)}
                >
                  <Megaphone className="h-3 w-3 mr-1" />
                  Announce
                </Button>
              )}
            </div>
          </div>

          {/* Channels List */}
          <div className="flex-1 overflow-y-auto">
            {/* Starred Channels Section */}
            {userPreferences.starred_channels?.length > 0 && (
              <div className="p-3">
                <div className="flex items-center gap-1 text-sm font-semibold text-muted-foreground mb-2">
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  Starred
                </div>
                <div className="space-y-1">
                  {filteredChannels
                    .filter(c => userPreferences.starred_channels?.includes(c.id))
                    .map((channel) => (
                      <button
                        key={channel.id}
                        onClick={() => setSelectedChannel(channel)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors ${
                          selectedChannel?.id === channel.id
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                      >
                        {getChannelIcon(channel.type)}
                        <span className="flex-1 text-left truncate">{channel.name}</span>
                        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Channels Section */}
            <div className="p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1 text-sm font-semibold text-muted-foreground">
                  <ChevronDown className="h-4 w-4" />
                  Channels
                </div>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setChannelDialogOpen(true)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-1">
                {filteredChannels
                  .filter(c => !userPreferences.starred_channels?.includes(c.id))
                  .map((channel) => (
                    <button
                      key={channel.id}
                      onClick={() => setSelectedChannel(channel)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors ${
                        selectedChannel?.id === channel.id
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      } ${userPreferences.muted_channels?.includes(channel.id) ? 'opacity-50' : ''}`}
                    >
                      {getChannelIcon(channel.type)}
                      <span className="flex-1 text-left truncate">{channel.name}</span>
                      {userPreferences.muted_channels?.includes(channel.id) && (
                        <Bell className="h-3 w-3 line-through" />
                      )}
                      {(ws.unreadCounts[channel.id] > 0 || channel.unread_count > 0) && (
                        <Badge variant="destructive" className="h-5 px-1.5 text-xs">
                          {ws.unreadCounts[channel.id] || channel.unread_count}
                        </Badge>
                      )}
                    </button>
                  ))}
              </div>
            </div>

            {/* Direct Messages Section */}
            <div className="p-3">
              <div className="flex items-center gap-1 text-sm font-semibold text-muted-foreground mb-2">
                <ChevronDown className="h-4 w-4" />
                Direct Messages
              </div>

              <div className="space-y-1">
                <button
                  onClick={() => setNewDMDialogOpen(true)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span>New Message</span>
                </button>
                
                {directMessages.length === 0 ? (
                  <div className="text-xs text-muted-foreground text-center py-2">
                    No direct messages yet
                  </div>
                ) : (
                  directMessages.map((dm) => {
                    // Detect Prime DM channel
                    const isPrimeChannel = dm.is_prime_channel || dm.members?.includes('prime-system') || dm.name?.includes('dm-prime');
                    const userName = isPrimeChannel ? '🔱 Prime' : (dm.other_user_name || dm.description?.replace('Direct message with ', '') || 'Unknown User');
                    // Ensure the dm object has other_user_name when selected
                    const enrichedDM = { ...dm, other_user_name: userName, is_prime_channel: isPrimeChannel };
                    return (
                      <button
                        key={dm.id}
                        onClick={() => setSelectedChannel(enrichedDM)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors ${
                          selectedChannel?.id === dm.id
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                      >
                        <div className="relative">
                          <Avatar className="h-5 w-5">
                            <AvatarFallback className={`text-xs ${isPrimeChannel ? 'bg-purple-500 text-white' : 'bg-primary text-primary-foreground'}`}>
                              {userName.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                        </div>
                        <span className="flex-1 text-left truncate">{userName}</span>
                        {dm.unread_count > 0 && (
                          <Badge variant="destructive" className="h-5 px-1.5 text-xs">
                            {dm.unread_count}
                          </Badge>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* User Status Section */}
          <div className="border-t p-3">
            <div className="flex items-center gap-2">
              <div className="relative flex-shrink-0">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                    {user?.first_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <PresenceIndicator 
                  status={userStatus?.status || 'online'} 
                  size="xs"
                  className="absolute -bottom-0.5 -right-0.5"
                />
              </div>
              <div className="flex-1 min-w-0 truncate">
                <UserStatusSelector 
                  currentStatus={userStatus} 
                  onStatusChange={setUserStatus}
                />
              </div>
              {chatPermissions?.permissions?.is_admin && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 flex-shrink-0"
                  onClick={() => setAdminPanelOpen(true)}
                  title="Admin Panel"
                >
                  <Shield className="h-4 w-4" />
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 flex-shrink-0"
                onClick={() => setNotificationSettingsOpen(true)}
                title="Notification settings"
              >
                <Bell className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Main Chat Area */}
        {selectedChannel ? (
          <div className="flex-1 flex flex-col h-full overflow-hidden">
            {/* Channel Header */}
            <div className="h-14 border-b px-4 flex items-center justify-between bg-card flex-shrink-0">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {getChannelIcon(selectedChannel.type)}
                <div className="min-w-0 flex-1">
                  <h2 className="font-semibold truncate">
                    {selectedChannel.type === 'direct' 
                      ? (() => {
                          // For DMs, show the other user's name, never the internal ID
                          if (selectedChannel.other_user_name) {
                            return selectedChannel.other_user_name;
                          }
                          // Extract from description
                          const desc = selectedChannel.description || '';
                          const match = desc.match(/Direct message with (.+)/);
                          if (match && match[1]) {
                            return match[1];
                          }
                          // Last resort - never show the dm-xxx ID
                          return 'Direct Message';
                        })()
                      : selectedChannel.name
                    }
                  </h2>
                  {selectedChannel.type !== 'direct' && selectedChannel.description && (
                    <p className="text-xs text-muted-foreground truncate">{selectedChannel.description}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Connection Status */}
                {ws.isConnected ? (
                  <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 mr-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span>Live</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-xs text-yellow-600 dark:text-yellow-400 mr-2">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                    <span>Connecting...</span>
                  </div>
                )}
                
                <Button variant="ghost" size="icon" onClick={() => setChannelSettingsOpen(true)} title="Channel members">
                  <Users className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={generateSummary} title="AI Summary">
                  <Sparkles className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} title="Search messages">
                  <Search className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon">
                  <Pin className="h-4 w-4" />
                </Button>
                
                {/* Phase 5: Voice/Video/Huddle Buttons */}
                <div className="border-l pl-2 ml-1">
                  <VideoQuickCallButtons
                    onVoiceCall={() => setVoiceCallOpen(true)}
                    onVideoCall={() => {
                      console.log('Opening pre-call settings, selectedChannel:', selectedChannel);
                      if (selectedChannel?.type === 'direct' && selectedChannel?.other_user_id) {
                        setPendingCallTarget({
                          type: 'one_on_one',
                          participantIds: [selectedChannel.other_user_id],
                          channelId: null
                        });
                      } else if (selectedChannel) {
                        setPendingCallTarget({
                          type: 'group',
                          participantIds: [],
                          channelId: selectedChannel.id
                        });
                      }
                      setShowPreCallSettings(true);
                    }}
                    disabled={!selectedChannel}
                  />
                </div>
                
                <Button variant="ghost" size="icon" onClick={() => setChannelSettingsOpen(true)} title="Channel settings">
                  <Settings className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No messages yet</h3>
                  <p className="text-sm text-muted-foreground">
                    Be the first to start the conversation in #{selectedChannel.name}
                  </p>
                </div>
              ) : (
                groupMessagesByDate(messages).map((item, index) => 
                  item.type === 'date' ? (
                    <DateDivider key={`date-${index}`} date={item.date} />
                  ) : (
                    <div key={item.message.id} className={`group flex gap-3 hover:bg-muted/50 rounded-lg p-2 -mx-2 ${item.grouped ? 'mt-1' : 'mt-4'}`}>
                      {!item.grouped && (
                        <Avatar className="h-9 w-9">
                          {item.message.sender_id === 'prime-system' || item.message.is_prime_auto_reply ? (
                            <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-purple-500 to-indigo-600 text-white text-lg font-bold">
                              🔱
                            </div>
                          ) : (
                            <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                              {item.message.sender_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </AvatarFallback>
                          )}
                        </Avatar>
                      )}
                      {item.grouped && <div className="w-9" />}
                      
                      <div className="flex-1 min-w-0">
                        {!item.grouped && (
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-sm">{item.message.sender_name}</span>
                            {item.message.sender_role === 'tester' && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-medium">
                                Platform Tester
                              </span>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {new Date(item.message.created_at).toLocaleTimeString([], { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </span>
                            {item.message.edited_at && (
                              <span className="text-xs text-muted-foreground italic">(edited)</span>
                            )}
                            {item.message.pinned && (
                              <Pin className="h-3 w-3 text-primary" />
                            )}
                            {item.message.pending && (
                              <span className="text-xs text-muted-foreground italic">Sending...</span>
                            )}
                          </div>
                        )}
                        
                        {/* Edit mode */}
                        {editingMessage && editingMessage.id === item.message.id ? (
                          <div className="space-y-2">
                            <Textarea
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              className="min-h-[60px]"
                              autoFocus
                            />
                            <div className="flex gap-2">
                              <Button size="sm" onClick={saveEditMessage}>Save</Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingMessage(null)}>Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {/* Message content with formatting */}
                            <div className={`text-sm whitespace-pre-wrap break-words ${item.message.pending ? 'opacity-60' : ''}`}>
                              {item.message.content.split(/(\*\*.*?\*\*|__.*?__|@\w+)/g).map((part, idx) => {
                                if (part.startsWith('**') && part.endsWith('**')) {
                                  return <strong key={idx}>{part.slice(2, -2)}</strong>;
                                } else if (part.startsWith('__') && part.endsWith('__')) {
                                  return <em key={idx}>{part.slice(2, -2)}</em>;
                                } else if (part.startsWith('@')) {
                                  return (
                                    <span key={idx} className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-1 rounded font-medium">
                                      {part}
                                    </span>
                                  );
                                }
                                return part;
                              })}
                            </div>

                            {/* File Attachments remain the same but I'll keep them here for completeness */}
                            {item.message.attachments && item.message.attachments.length > 0 && (
                              <div className="mt-2 space-y-2">
                                {item.message.attachments.map((att, idx) => (
                                  <div key={idx} className="border rounded-lg overflow-hidden max-w-md">
                                    {att.file_type === 'image' ? (
                                      <div className="relative group/image">
                                        <img 
                                          src={`${process.env.REACT_APP_BACKEND_URL}${att.url}`} 
                                          alt={att.filename}
                                          className="w-full h-auto max-h-80 object-contain bg-muted cursor-pointer"
                                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${att.url}`, '_blank')}
                                        />
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-3 p-3 bg-muted/50">
                                        <div className="p-2 bg-background rounded">
                                          <FileIcon className="h-6 w-6 text-primary" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium truncate">{att.filename}</p>
                                          <p className="text-xs text-muted-foreground">
                                            {(att.file_size / 1024).toFixed(1)} KB
                                          </p>
                                        </div>
                                        <a
                                          href={`${process.env.REACT_APP_BACKEND_URL}${att.url}`}
                                          download={att.filename}
                                        >
                                          <Button size="icon" variant="ghost" className="h-8 w-8">
                                            <Download className="h-4 w-4" />
                                          </Button>
                                        </a>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Linked Entities (Job/Contact) */}
                            {(item.message.linked_job || item.message.linked_contact) && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {item.message.linked_job && (
                                  <button
                                    onClick={() => window.open(`/jobs/${item.message.linked_job.id}`, '_blank')}
                                    className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                                  >
                                    <Briefcase className="h-3 w-3" />
                                    {item.message.linked_job.name}
                                    {item.message.linked_job.stage && (
                                      <span className="text-blue-500 dark:text-blue-400">• {item.message.linked_job.stage}</span>
                                    )}
                                  </button>
                                )}
                                {item.message.linked_contact && (
                                  <button
                                    onClick={() => window.open(`/contacts/${item.message.linked_contact.id}`, '_blank')}
                                    className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-md hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                                  >
                                    <User className="h-3 w-3" />
                                    {item.message.linked_contact.name}
                                  </button>
                                )}
                              </div>
                            )}

                            {/* System Message Badge */}
                            {item.message.message_type === 'system' && (
                              <div className="mt-1">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded">
                                  <Zap className="h-3 w-3" />
                                  {item.message.system_event?.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </span>
                              </div>
                            )}

                            {/* Prime Message Badge */}
                            {item.message.message_type === 'prime' && (
                              <div className="mt-1">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                                  🔱 Prime AI
                                </span>
                              </div>
                            )}

                            {/* Reactions */}
                            {item.message.reactions && item.message.reactions.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {item.message.reactions.map((reaction, idx) => (
                                  <Button
                                    key={idx}
                                    variant="outline"
                                    size="sm"
                                    className="h-6 px-2 text-xs"
                                    onClick={() => handleReactToMessage(item.message, reaction.emoji)}
                                  >
                                    {reaction.emoji} {reaction.count}
                                  </Button>
                                ))}
                              </div>
                            )}

                            {/* Thread Reply Count */}
                            {threadCounts[item.message.id] > 0 && (
                              <button 
                                onClick={() => handleReplyInThread(item.message)}
                                className="flex items-center gap-1 mt-2 text-xs text-primary hover:underline"
                              >
                                <MessageSquare className="h-3 w-3" />
                                {threadCounts[item.message.id]} {threadCounts[item.message.id] === 1 ? 'reply' : 'replies'}
                              </button>
                            )}
                          </>
                        )}
                      </div>

                      {/* Message Actions Menu */}
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="flex items-center gap-1">
                          <EmojiPicker 
                            onSelect={(emoji) => handleReactToMessage(item.message, emoji)}
                            trigger={
                              <Button variant="ghost" size="icon" className="h-7 w-7">
                                <Smile className="h-4 w-4" />
                              </Button>
                            }
                          />
                          <MessageActions
                            message={item.message}
                            currentUserId={user.id}
                            onEdit={handleEditMessage}
                            onDelete={handleDeleteMessage}
                            onReply={handleReplyInThread}
                            onReact={(msg) => {}} // Handled by emoji picker
                            onPin={handlePinMessage}
                            onCopyLink={handleCopyLink}
                          />
                        </div>
                      </div>
                    </div>
                  )
                )
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Thread Panel (conditional) */}
            {threadOpen && threadParentMessage && (
              <ThreadPanel
                parentMessage={threadParentMessage}
                channelId={selectedChannel.id}
                currentUser={user}
                onClose={() => {
                  setThreadOpen(false);
                  setThreadParentMessage(null);
                }}
              />
            )}

            {/* Original message input section continues below... */}
            {!threadOpen && (
              <>
                {/* Typing Indicator - keep existing code */}
                {ws.typingUsers && ws.typingUsers[selectedChannel.id] && ws.typingUsers[selectedChannel.id].typing_users && ws.typingUsers[selectedChannel.id].typing_users.length > 0 && (
                  <div className="px-4 py-2 bg-muted/30 border-t">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                        <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                        <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                      </div>
                      <span>
                        {ws.typingUsers[selectedChannel.id].typing_users.length === 1
                          ? `${ws.typingUsers[selectedChannel.id].typing_users[0]} is typing...`
                          : ws.typingUsers[selectedChannel.id].typing_users.length === 2
                          ? `${ws.typingUsers[selectedChannel.id].typing_users[0]} and ${ws.typingUsers[selectedChannel.id].typing_users[1]} are typing...`
                          : `${ws.typingUsers[selectedChannel.id].typing_users[0]} and ${ws.typingUsers[selectedChannel.id].typing_users.length - 1} others are typing...`
                        }
                      </span>
                    </div>
                  </div>
                )}

                {/* Message Input */}
                <div className="border-t p-4 bg-card flex-shrink-0">
                  {/* Mention Suggestions */}
                  {showMentionSuggestions && mentionSuggestions.length > 0 && (
                    <div className="absolute bottom-full left-4 right-4 mb-2 bg-popover border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {mentionSuggestions.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => insertMention(u)}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent transition-colors text-left"
                    >
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                          {u.name.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{u.name}</div>
                        <div className="text-xs text-muted-foreground truncate">@{u.username}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              <form onSubmit={handleSendMessage} className="relative">
                <Textarea
                  value={newMessage}
                  onChange={handleTyping}
                  placeholder="Enter chat here"
                  className="pr-24 resize-none"
                  rows={1}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e);
                    }
                  }}
                />
                <div className="absolute right-2 bottom-2 flex gap-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileUpload}
                    accept="image/*,video/*,.pdf,.doc,.docx,.txt"
                  />
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={handleFileSelect}
                    disabled={uploadingFile}
                    title="Upload file"
                  >
                    {uploadingFile ? (
                      <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                    ) : (
                      <Paperclip className="h-4 w-4" />
                    )}
                  </Button>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    title="Add emoji"
                  >
                    <Smile className="h-4 w-4" />
                  </Button>
                  <Button type="submit" size="icon" className="h-8 w-8">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </form>
              <p className="text-xs text-muted-foreground mt-2">
                <kbd className="px-1 py-0.5 bg-muted rounded">Enter</kbd> to send, 
                <kbd className="px-1 py-0.5 bg-muted rounded ml-1">Shift + Enter</kbd> for new line
              </p>
            </div>
              </>
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-center p-8">
            <div>
              <MessageSquare className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Welcome to Company Chat</h3>
              <p className="text-muted-foreground">
                Select a channel from the sidebar to start chatting with your team
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Create Channel Dialog */}
      <Dialog open={channelDialogOpen} onOpenChange={setChannelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create a Channel</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Channel Name *</label>
              <Input
                value={channelForm.name}
                onChange={(e) => setChannelForm({ ...channelForm, name: e.target.value })}
                placeholder="e.g. marketing-team"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Description</label>
              <Textarea
                value={channelForm.description}
                onChange={(e) => setChannelForm({ ...channelForm, description: e.target.value })}
                placeholder="What's this channel about?"
                rows={3}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Channel Type</label>
              <select
                value={channelForm.type}
                onChange={(e) => setChannelForm({ ...channelForm, type: e.target.value })}
                className="w-full px-3 py-2 border rounded-md bg-background"
              >
                <option value="public">Public - Anyone can join</option>
                <option value="private">Private - Invite only</option>
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setChannelDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateChannel}>Create Channel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Thread Sidebar */}
      {threadOpen && threadParentMessage && (
        <div className="fixed right-0 top-0 bottom-0 w-96 bg-background border-l shadow-xl z-50 flex flex-col">
          {/* Thread Header */}
          <div className="border-b p-4 flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Thread
            </h3>
            <Button variant="ghost" size="icon" onClick={closeThread}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Parent Message */}
          <div className="border-b p-4 bg-muted/30">
            <div className="flex gap-3">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {threadParentMessage.sender_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-sm">{threadParentMessage.sender_name}</span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(threadParentMessage.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="text-sm mt-1 whitespace-pre-wrap break-words">
                  {threadParentMessage.content}
                </div>
              </div>
            </div>
          </div>

          {/* Thread Replies */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {threadMessages.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground mt-8">
                No replies yet. Be the first to reply!
              </div>
            ) : (
              threadMessages.map((reply) => (
                <div key={reply.id} className="flex gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {reply.sender_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="font-semibold text-sm">{reply.sender_name}</span>
                      {reply.sender_role === 'tester' && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-medium">
                          Platform Tester
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {new Date(reply.created_at).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </span>
                    </div>
                    <div className="text-sm mt-1 whitespace-pre-wrap break-words">
                      {reply.content}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Thread Reply Input */}
          <div className="border-t p-4 bg-card">
            <form onSubmit={sendThreadReply}>
              <Textarea
                value={threadReplyText}
                onChange={(e) => setThreadReplyText(e.target.value)}
                placeholder="Reply in thread..."
                className="mb-2 resize-none"
                rows={3}
              />
              <div className="flex justify-end">
                <Button type="submit" size="sm" disabled={!threadReplyText.trim()}>
                  <Send className="h-4 w-4 mr-1" />
                  Send
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New DM Dialog */}
      <Dialog open={newDMDialogOpen} onOpenChange={setNewDMDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Direct Message</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Select User</label>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="w-full mt-1 p-2 border rounded-md bg-background text-foreground border-input focus:ring-2 focus:ring-primary"
              >
                <option value="" className="bg-background text-foreground">Choose a user...</option>
                {users.filter(u => u.id !== user.id).map((u) => (
                  <option key={u.id} value={u.id} className="bg-background text-foreground">
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewDMDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => createOrGetDM(selectedUserId)} disabled={!selectedUserId}>
              Start Chat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Summary Dialog */}
      <Dialog open={summaryDialogOpen} onOpenChange={setSummaryDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-yellow-500" />
              AI Channel Summary
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {generatingSummary ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mb-4" />
                <p className="text-sm text-muted-foreground">Generating summary...</p>
              </div>
            ) : (
              <div className="prose dark:prose-invert max-w-none">
                <div className="whitespace-pre-wrap text-sm">
                  {summary || 'No summary available.'}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSummaryDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Read Receipts Dialog */}
      <Dialog open={readReceiptsDialogOpen} onOpenChange={setReadReceiptsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Read Receipts
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {readReceipts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No one has read this message yet
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {readReceipts.map((receipt) => (
                  <div key={receipt.user_id} className="flex items-center gap-3 p-2 rounded hover:bg-accent">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {receipt.name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{receipt.name}</div>
                      <div className="text-xs text-muted-foreground">
                        Read {new Date(receipt.read_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="text-blue-500">
                      <Eye className="h-4 w-4" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReadReceiptsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Search Dialog */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Search Messages</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Search in all channels..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  handleSearch(e.target.value);
                }}
                className="flex-1"
              />
              {searching && <div className="animate-spin h-5 w-5 border-2 border-current border-t-transparent rounded-full mt-2" />}
            </div>

            <div className="max-h-96 overflow-y-auto space-y-2">
              {searchResults.length === 0 && searchQuery.length >= 2 && !searching ? (
                <div className="text-center py-8 text-muted-foreground">
                  No messages found
                </div>
              ) : searchResults.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Type to search messages...
                </div>
              ) : (
                searchResults.map((result) => (
                  <button
                    key={result.id}
                    onClick={() => jumpToMessage(result)}
                    className="w-full text-left p-3 border rounded-lg hover:bg-accent transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{result.sender_name}</span>
                          <span className="text-xs text-muted-foreground">
                            in #{result.channel_name}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(result.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground truncate">
                          {result.content}
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Phase 2: Channel Settings Dialog */}
      <ChannelSettings
        channel={selectedChannel}
        isOpen={channelSettingsOpen}
        onClose={() => setChannelSettingsOpen(false)}
        onUpdate={() => {
          fetchChannels();
          fetchUserPreferences();
        }}
        currentUser={user}
        users={users}
      />

      {/* Phase 2: Notification Settings Dialog */}
      <NotificationSettings
        isOpen={notificationSettingsOpen}
        onClose={() => setNotificationSettingsOpen(false)}
      />

      {/* Phase 4: Admin Panel */}
      <AdminPanel
        isOpen={adminPanelOpen}
        onClose={() => setAdminPanelOpen(false)}
        currentUser={user}
      />

      {/* Phase 3: Advanced Search */}
      <AdvancedSearch
        isOpen={advancedSearchOpen}
        onClose={() => setAdvancedSearchOpen(false)}
        channels={channels}
        users={users}
        onResultClick={(result) => {
          const channel = channels.find(c => c.id === result.channel_id);
          if (channel) {
            setSelectedChannel(channel);
          }
        }}
      />

      {/* Phase 3: Saved Messages */}
      <SavedMessages
        isOpen={savedMessagesOpen}
        onClose={() => setSavedMessagesOpen(false)}
        onMessageClick={(message) => {
          const channel = channels.find(c => c.id === message.channel_id);
          if (channel) {
            setSelectedChannel(channel);
          }
        }}
      />

      {/* Phase 3: Create Announcement */}
      <CreateAnnouncementDialog
        isOpen={createAnnouncementOpen}
        onClose={() => setCreateAnnouncementOpen(false)}
        channels={channels}
        onCreated={() => {
          fetchAnnouncements();
          fetchMessages(selectedChannel?.id);
        }}
      />

      {/* Phase 5: Voice, Video, Huddles (Future-ready) */}
      <VoiceCallDialog
        isOpen={voiceCallOpen}
        onClose={() => setVoiceCallOpen(false)}
        participant={selectedChannel?.type === 'direct' ? selectedChannel?.other_user_name : null}
        channelName={selectedChannel?.name}
      />

      <VideoCallDialog
        isOpen={videoCallOpen}
        onClose={() => setVideoCallOpen(false)}
        participant={selectedChannel?.type === 'direct' ? selectedChannel?.other_user_name : null}
        channelName={selectedChannel?.name}
      />

      <HuddleDialog
        isOpen={huddleOpen}
        onClose={() => setHuddleOpen(false)}
        channelName={selectedChannel?.name}
        participants={[]}
      />
      
      {/* Pre-Call Settings Dialog - shows device selection before starting call */}
      {showPreCallSettings && pendingCallTarget && (
        <PreCallSettings
          onStart={async (settings) => {
            setShowPreCallSettings(false);
            if (settings.stream && pendingCallTarget) {
              try {
                await videoCall.startCallWithStream(
                  pendingCallTarget.participantIds,
                  pendingCallTarget.channelId,
                  pendingCallTarget.type,
                  settings.stream
                );
              } catch (error) {
                console.error('Error starting call:', error);
              }
            }
            setPendingCallTarget(null);
          }}
          onCancel={() => {
            setShowPreCallSettings(false);
            setPendingCallTarget(null);
          }}
          callType="video"
        />
      )}
      
      {/* Video call UI is now rendered globally via GlobalVideoCall component */}
    </Layout>
  );
};
